'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import { CmsBannersListItem } from '@actr/stores/src/stores/models/seo/banners';
import { Container } from '@actr/ui/src/components/container';
import ApplicationBanner from '@actr/widgets/src/components/seo/application-banner';
import { MainPageAdvantagesRedesign } from '@actr/widgets/src/components/seo/main-page-advantages-redesign';
import { MainPageStats } from '@actr/widgets/src/components/seo/main-page-stats';
import { MainPageTopInfo } from '@actr/widgets/src/components/seo/main-page-top-info';
import { observer } from 'mobx-react-lite';
import React, { FC } from 'react';

import SearchFormHeader from '@/components/avia/search-form-header';
import MainPageBanners from '@/components/common/main-page-banners';
import AccountFeatures from '@/components/page-constucts/account-features';
import CheapestDirectionsBlock from '@/components/page-constucts/cheapest-directions-block';
import DestinationsBlock from '@/components/page-constucts/destinations-block';
import HotOffersBlock from '@/components/page-constucts/hot-offers-block';
import VisaFreeDestinations from '@/components/page-constucts/visa-free-destinations';
import aviaMainPageYMLogger, { AviaMainPageYMLogs } from '@/logs/ym/avia/main-page';
import type { HotOffer } from '@/services/models/avia/hot-offers';
import type { MainPageInfo } from '@/services/models/avia/main-page-info';

import * as Styled from './style.css';

export type BaseAviaSearchPageProps = {
  banners: CmsBannersListItem[];
  pageInfo: MainPageInfo | null;
  hotOffers: HotOffer[];
}

const BaseAviaSearchPage: FC<BaseAviaSearchPageProps> = observer(({ banners, pageInfo, hotOffers }) => {
  const {
    content,
    info: { title = '', description = '' } = {},
    search_form_title = 'Куда хотите полететь?'
  } = pageInfo || {};

  const { popular_directions, popular_countries, popular_cities, visa_free_destinations, cheapest_directions } = content || {};

  const infoTitle = title || 'Дешевые авиабилеты онлайн — легко, быстро и выгодно с Aviakassa.com';
  const infoDescription = description || 'Хочется найти действительно дешевые авиабилеты без лишних хлопот? '
    + 'Aviakassa.com — это ваш идеальный помощник в поиске выгодных предложений на авиаперелеты. Мы понимаем, '
    + 'как важно экономить время и деньги, поэтому сделали процесс бронирования простым и удобным.';

  function handleBannerClick() {
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.CLICK_BANNER);
  }

  return (
    <>
      <SearchFormHeader
        title={search_form_title}
        withNavigation={true}
      />
      <Container>
        <div className={Styled.MainPageWrapper}>
          {!!hotOffers.length && (
            <HotOffersBlock offers={hotOffers.slice(0, 10)} />
          )}
          <ApplicationBanner isMainPage={true} />
          <MainPageBanners
            items={banners}
            revalidateUrl="/internal-api/cms/update-main-page"
            onBannerClick={handleBannerClick}
          />
          <AccountFeatures />
          <MainPageTopInfo title={infoTitle} description={infoDescription} />
          {!!cheapest_directions?.list?.recommendations?.length && (
          <CheapestDirectionsBlock data={cheapest_directions} />
          )}
          { popular_countries?.list.length && (
          <DestinationsBlock
            data={popular_countries}
            buttonRoute="/countries"
            buttonRouteText="Все страны"
          />
          ) }
          { popular_directions?.list.length && (
          <DestinationsBlock
            data={popular_directions}
            buttonRoute="/routes"
            buttonRouteText="Все направления"
          />
          ) }
          { popular_cities?.list.length && (
          <DestinationsBlock
            data={popular_cities}
            className={Styled.CitiesWrapper}
          />
          )}
          { visa_free_destinations?.list.length && (
            <VisaFreeDestinations data={visa_free_destinations} />
          )}
          <MainPageStats />
          <MainPageAdvantagesRedesign
            easyBookTitle="Почему бронировать с Aviakassa — это удобно"
          />
        </div>
      </Container>
    </>
  );
});
export default loadDIModules(BaseAviaSearchPage, aviaSearchDIModule);
